import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { tokenizeSql, type SqlToken } from "@/chat/sql";
import { CopyIcon, ExternalIcon } from "@/components/icons";
import { cn } from "@/lib/cn";

/**
 * One SQL statement in the trace: highlighted, copyable, and linked into the
 * schema browser (PRD FR-5.2).
 *
 * Every colour is a token — keywords take the accent, strings the success
 * colour, comments the disabled ink — so a rebrand reaches the trace along with
 * everything else, and no highlighting library needs to be themed twice.
 *
 * The relation links are the part worth the parse. `sources[]` is a claim about
 * the answer; a link on the table name in the statement that ran is the same
 * claim a reader can follow in one click.
 */

const TOKEN_CLASS: Record<SqlToken["kind"], string> = {
  keyword: "text-accent font-semibold",
  function: "text-info",
  string: "text-success",
  number: "text-warning",
  comment: "text-ink-disabled italic",
  operator: "text-ink-secondary",
  relation: "text-ink underline decoration-dotted underline-offset-2",
  identifier: "text-ink",
  plain: "text-ink-secondary",
};

export function SqlBlock({
  sql,
  relations = [],
  connectionId,
  tone = "default",
}: {
  sql: string;
  relations?: string[];
  connectionId?: string | null;
  tone?: "default" | "blocked";
}) {
  const [copied, setCopied] = useState(false);
  const tokens = tokenizeSql(sql, relations);

  return (
    <div
      className={cn(
        "rounded-element flex items-start gap-2 border p-2.5",
        tone === "blocked" ? "border-error bg-error-subtle" : "border-border bg-surface",
      )}
    >
      <pre className="type-code min-w-0 flex-1 overflow-x-auto whitespace-pre-wrap">
        {tokens.map((token, index) =>
          token.relation && connectionId ? (
            <Link
              key={index}
              to="/sources/$connectionId"
              params={{ connectionId }}
              search={{ tab: "schema" as const, relation: token.relation }}
              title={`Open ${token.relation} in the schema browser`}
              className={cn(TOKEN_CLASS.relation, "hover:text-accent")}
            >
              {token.text}
            </Link>
          ) : (
            <span key={index} className={TOKEN_CLASS[token.kind]}>
              {token.text}
            </span>
          ),
        )}
      </pre>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(sql).then(() => {
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          });
        }}
        className={cn(
          "type-caption text-ink-secondary hover:text-ink hover:bg-hover motion-fast",
          "rounded-inner inline-flex shrink-0 items-center gap-1 px-2 py-1",
        )}
      >
        <CopyIcon className="size-3" />
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

/** The relations a step touched, as links (PRD FR-5.2, FR-5.3). */
export function RelationLinks({
  relations,
  connectionId,
}: {
  relations: string[];
  connectionId?: string | null;
}) {
  if (!relations.length || !connectionId) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="type-caption text-ink-disabled">reads</span>
      {relations.map((relation) => (
        <Link
          key={relation}
          to="/sources/$connectionId"
          params={{ connectionId }}
          search={{ tab: "schema" as const, relation }}
          className={cn(
            "type-caption text-ink-secondary hover:text-accent hover:bg-hover motion-fast",
            "rounded-full inline-flex items-center gap-1 px-2 py-0.5 normal-case",
          )}
        >
          {relation}
          <ExternalIcon className="size-3" />
        </Link>
      ))}
    </div>
  );
}
